/**
 * #1067 — the Shift+S multiplier row: how many times the one-click size each
 * leg of a Shift+S press is sent at.
 *
 * There is no default (Citadel named none), so a blank field is a real state:
 * Shift+S is unavailable and the row says so. The field takes the raw text and
 * decides; it never coerces. Anything that is not a whole number 2 or more
 * CLEARS the stored multiplier rather than leaving the previous one live
 * behind a field showing something else — the visible field and what the key
 * would send must never disagree (#607: an input that reinterprets what was
 * typed once widened a live limit 100x).
 *
 * The status line beneath names the chord, the multiplier and the per-side
 * quantity it produces at the current one-click size, or why it is unavailable.
 */
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { isValidTwoSidedMultiplier, useDisplaySettings } from '@/stores/displaySettings';
import { oneClickCeiling, useOneClickStore } from '@/stores/oneClickStore';
import {
  useHotkeyLabel,
  useShiftTwoSidedChordLabel,
  useTwoSidedMultiplier,
} from '@/hooks/useLiveHotkeys';

const WHOLE_NUMBER = /^\d+$/;

export function TwoSidedMultiplierSetting({ twoSidedOn }: { twoSidedOn: boolean }) {
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const oneClickMax = useAuthStore((s) => s.user?.oneClickMaxContracts ?? null);
  const setMultiplier = useDisplaySettings((s) => s.setLt2TwoSidedMultiplier);
  const multiplier = useTwoSidedMultiplier();
  const sizeContracts = useOneClickStore((s) => s.sizeContracts);
  const chord = useShiftTwoSidedChordLabel();
  const twoSidedKey = useHotkeyLabel('two-sided');
  const stored = multiplier.ok ? String(multiplier.multiplier) : '';
  // The trader's own text while they edit, tied to the stored value it left
  // behind and to who typed it. Any OTHER change — another tab setting a
  // value, another trader signing in — no longer matches, and the field snaps
  // to the live value: it must never show "unavailable" over a multiplier
  // that fires. Its own writes match by construction, so "4x" stays visible.
  const [draft, setDraft] = useState<{ text: string; basis: string; viewerId: string | null } | null>(
    null,
  );
  const text = draft != null && draft.basis === stored && draft.viewerId === viewerId ? draft.text : stored;
  const parses = (raw: string) => WHOLE_NUMBER.test(raw) && isValidTwoSidedMultiplier(Number(raw));
  const invalid = text !== '' && !parses(text);

  const chordName = chord ?? '⇧S';
  const ceiling = oneClickCeiling({ oneClickMaxContracts: oneClickMax });
  // No valid server ceiling means one-click cannot be armed at all; the
  // number shown is the client's fallback, and says so rather than naming a
  // limit this trader does not have.
  const limitName = oneClickMax == null ? `${ceiling}-contract fallback limit` : `${ceiling} one-click limit`;
  const status = ((): { tone: 'ok' | 'warn'; text: string } => {
    if (!twoSidedOn) return { tone: 'warn', text: `Unavailable — turn on Post both sides above first.` };
    if (chord == null) {
      return {
        tone: 'warn',
        text:
          twoSidedKey === '—'
            ? 'Unavailable — Post both sides has no key, so there is no Shift chord.'
            : `Unavailable — Post both sides is on ${twoSidedKey}. The Shift chord works only with an A–Z letter key.`,
      };
    }
    if (invalid) return { tone: 'warn', text: `Not saved — enter a whole number 2 or more. ${chordName} is unavailable.` };
    if (!multiplier.ok) return { tone: 'warn', text: `Unavailable — enter a whole number 2 or more.` };
    const perSide = sizeContracts * multiplier.multiplier;
    if (perSide > ceiling) {
      return {
        tone: 'warn',
        text: `${chordName} = ${multiplier.multiplier}× of ${sizeContracts} = ${perSide} per side, over your ${limitName} — it will refuse until the size or multiplier is lower.`,
      };
    }
    return {
      tone: 'ok',
      text: `${chordName} = ${multiplier.multiplier}× of ${sizeContracts} = ${perSide} per side (${perSide * 2} total).`,
    };
  })();

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          aria-label={`${chordName} size multiplier`}
          aria-invalid={invalid}
          value={text}
          placeholder="not set"
          disabled={!twoSidedOn}
          onChange={(e) => {
            // Raw, untrimmed: a stray space is invalid, never silently dropped.
            const raw = e.target.value;
            if (parses(raw)) {
              setMultiplier(Number(raw), viewerId);
            } else {
              // Blank or invalid: fail closed, so Shift+S cannot fire at a
              // value the field no longer shows.
              setMultiplier(null, viewerId);
            }
            // The basis is what the store reads AFTER this write, so the draft
            // survives exactly its own write and nothing else.
            const after = useDisplaySettings.getState();
            const basis =
              after.lt2TwoSidedMultiplier != null && after.lt2TwoSidedMultiplierOwnerId === viewerId
                ? String(after.lt2TwoSidedMultiplier)
                : '';
            setDraft({ text: raw, basis, viewerId });
          }}
          onBlur={() => {
            if (!invalid) setDraft(null);
          }}
          className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-24 rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none disabled:opacity-50"
        />
        <span className="text-muted-foreground text-xs">× one-click size, per side</span>
      </div>
      <p
        data-testid="two-sided-multiplier-status"
        className={status.tone === 'ok' ? 'text-primary text-xs' : 'text-xs text-amber-400'}
      >
        {status.text}
      </p>
    </div>
  );
}
